/* Kaufleiste beim Scrollen: erscheint, sobald der Kaufbereich oben aus dem Bild ist, und kauft ueber dessen Formular. */

class KgStickyBuy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-add]');
    this.name = this.querySelector('[data-sticky-name]');
    this.priceBox = this.querySelector('[data-sticky-price]');
    this.image = this.querySelector('[data-sticky-image] img');

    this.button?.addEventListener('click', () => this.kaufen());
    this.onVariant = (event) => this.aktualisieren(event.detail?.variant);
    document.addEventListener('variant:change', this.onVariant);

    /* Nach einem weichen Seitenwechsel steht ein neuer Kaufbereich im Dokument */
    this.onReplaced = () => {
      this.beobachten();
      this.endeBeobachten();
    };
    document.addEventListener('page:replaced', this.onReplaced);

    this.messen = () => this.hoeheMelden();
    window.addEventListener('resize', this.messen);

    /*
      Am Ende der Bewegung die Leistenfarbe neu messen lassen — frueher als der
      Zeitgeber in `zeigen()`, der nur das Netz darunter ist. Bewusst ohne eigene
      Methode: `messen` ist hier schon vergeben, und eine gleichnamige Klassenmethode
      wuerde von dieser Zuweisung verdeckt.
    */
    this.aufBewegungsende = (ereignis) => {
      if (ereignis.target === this && ereignis.propertyName === 'translate') neuMessenLassen();
    };
    this.addEventListener('transitionend', this.aufBewegungsende);

    /* Bei laengerem Namen oder Umbruch aendert sich die Hoehe auch ohne Fensterwechsel */
    if ('ResizeObserver' in window) {
      this.groesse = new ResizeObserver(() => this.hoeheMelden());
      this.groesse.observe(this);
    }

    this.beobachten();
    this.zustandSpiegeln();

    /* Im Editor zeigt sich die Leiste, sobald ihr Abschnitt angewaehlt ist */
    if (window.Shopify?.designMode) {
      this.onSelect = (event) => {
        if (event.target.contains(this)) this.vorschau(event.type === 'shopify:section:select');
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onSelect);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.endeObserver?.disconnect();
    this.groesse?.disconnect();
    document.removeEventListener('variant:change', this.onVariant);
    document.removeEventListener('page:replaced', this.onReplaced);
    window.removeEventListener('resize', this.messen);
    this.removeEventListener('transitionend', this.aufBewegungsende);
    if (this.onSelect) {
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onSelect);
    }
    document.documentElement.style.removeProperty('--sticky-bar-height');
    document.documentElement.style.removeProperty('--sticky-bar-side-bottom');
  }

  get form() {
    return document.querySelector('form[data-product-form]');
  }

  get echterKnopf() {
    return document.querySelector('[data-add-to-cart]');
  }

  /* Kaufen, solange es geht, sonst der Benachrichtigen-Button bei ausverkaufter Sorte */
  get zielKnopf() {
    return this.echterKnopf || document.querySelector('[data-notify-button]');
  }

  beobachten() {
    this.observer?.disconnect();
    /*
      Beobachtet wird der ganze Produktbereich, nicht nur der Kaufen-Knopf.

      Sie erscheint, wenn der Bereich als Ganzes vorbei ist und die naechste Section beginnt.
      Am Knopf allein gemessen kaeme sie mitten im Produktbereich, waehrend Beschreibung,
      Naehrwerte und Fragen noch folgen — dort ist sie verfrueht: Wer noch im Produkt liest,
      hat den Kauf nicht hinter sich gelassen.

      Faellt `.main-product` aus, bleibt der Kaufen-Knopf als Anker — etwa im Schnellkauf.
    */
    const anker = document.querySelector('.main-product') || document.querySelector('[data-buy-buttons]');
    if (!anker) {
      this.zeigen(false);
      return;
    }
    this.observer = new IntersectionObserver(
      ([eintrag]) => {
        /* Nur zeigen, wenn der Bereich nach oben herausgelaufen ist, nicht wenn er erst kommt */
        const daruber = eintrag.boundingClientRect.bottom < 0;
        this.hinterKaufbereich = !eintrag.isIntersecting && daruber;
        this.pruefen();
      },
    );
    this.observer.observe(anker);
    this.endeBeobachten();
  }

  /*
    Am Seitenende geht die Leiste aus dem Weg, sonst liegen die Fusszeilen-Links
    (Impressum und die anderen Rechtstexte) dauerhaft darunter.
  */
  endeBeobachten() {
    this.endeObserver?.disconnect();
    const fuss = document.querySelector('.footer') || document.querySelector('#shopify-section-footer');
    if (!fuss) return;
    this.endeObserver = new IntersectionObserver(
      ([eintrag]) => {
        this.amEnde = eintrag.isIntersecting;
        this.pruefen();
      },
      { threshold: 0 },
    );
    this.endeObserver.observe(fuss);
  }

  pruefen() {
    this.zeigen(Boolean(this.hinterKaufbereich) && !this.amEnde);
  }

  zeigen(an) {
    /* Ohne Kauf- und ohne Benachrichtigen-Button gibt es nichts anzubieten */
    const moeglich = an && Boolean(this.zielKnopf);
    if (moeglich === this.hasAttribute('data-visible')) return;
    this.toggleAttribute('data-visible', moeglich);
    this.hoeheMelden();
    /*
      In beide Richtungen erst nach der Bewegung messen lassen. Safari liest das zuletzt
      gezeichnete Bild: Mitten im Ausfahren stuende die Leiste dort noch, mitten im
      Einfahren stuende sie noch nicht da. Beides ergaebe die falsche Farbe.

      Bewusst ein fester Wert und nicht das Ende des Uebergangs: Am Ereignis gemessen
      raeumte die Leiste nicht mehr zuverlaessig ab, am Geraet geprueft.
    */
    window.setTimeout(neuMessenLassen, 320);
  }

  vorschau(an) {
    if (!an) {
      /* Der Beobachter meldet sich beim Neuaufsetzen sofort mit dem echten Zustand */
      this.beobachten();
      return;
    }
    this.toggleAttribute('data-visible', true);
    this.hoeheMelden();
  }

  hoeheMelden() {
    /*
      Gemeldet wird die belegte Zone unten: Hoehe plus Abstand zum Fensterrand.
      Gerechnet statt gemessen, weil die Leiste beim Erscheinen noch verschoben ist.
    */
    const abstand = parseFloat(getComputedStyle(this).bottom) || 0;
    const hoch = this.hasAttribute('data-visible') ? Math.round(this.offsetHeight + abstand) : 0;
    /* Die Seitenlage haengt auch an der Fensterbreite, also immer pruefen */
    this.seiteMelden(abstand);
    if (hoch === this.gemeldet) return;
    this.gemeldet = hoch;
    document.documentElement.style.setProperty('--sticky-bar-height', `${hoch}px`);
    /* Andere Teile rechnen mit: Nach-oben-Pfeil, Toast, Seitenende und die Erscheinungseffekte */
    document.dispatchEvent(new CustomEvent('kg:sticky-bar', { detail: { height: hoch } }));
  }

  /*
    Neben der Karte ist am Desktop oft Platz. Dann sitzt der Nach-oben-Pfeil auf gleicher
    Hoehe daneben statt darueber. Bei voller Breite oder engem Fenster bleibt er oberhalb.
  */
  seiteMelden(abstand) {
    const wurzel = document.documentElement.style;
    const karte = this.querySelector('.sticky-buy__inner');
    const knopf = document.querySelector('.scroll-top__button');
    const luft = karte ? (document.documentElement.clientWidth - karte.offsetWidth) / 2 : 0;
    const hoehe = knopf?.offsetHeight || 48;
    /*
      Bewusst ohne data-visible: Der Pfeil soll seine Hoehe behalten, auch wenn die
      Leiste gerade aus ist, sonst springt er beim Ein- und Ausblenden.
    */
    const passt = !this.classList.contains('sticky-buy--wide') && luft >= hoehe + 32;
    if (!passt) {
      wurzel.removeProperty('--sticky-bar-side-bottom');
      return;
    }
    const mitte = Math.round(abstand + (this.offsetHeight - hoehe) / 2);
    wurzel.setProperty('--sticky-bar-side-bottom', `${Math.max(16, mitte)}px`);
  }

  zustandSpiegeln() {
    const ziel = this.zielKnopf;
    if (!this.button) return;
    if (!ziel) {
      this.zeigen(false);
      return;
    }
    this.button.disabled = ziel.disabled;
    this.button.textContent = ziel.textContent.trim();
  }

  aktualisieren(variant) {
    if (!variant) return;

    if (this.name) {
      const quelle = this.dataset.titleSource || 'variant';
      const produkt = this.dataset.productTitle || '';
      /*
        Bei einem Bundle bleibt es beim Namen des Bundles: Die gewaehlte Sorte gehoert dort nur
        zu einem Bestandteil und waere als einzige Angabe irrefuehrend.
      */
      const sorte = this.hasAttribute('data-bundle')
        ? ''
        : variant.title && !variant.title.includes('Default')
          ? variant.title
          : '';
      if (quelle === 'product') this.name.textContent = produkt;
      else if (quelle === 'both') this.name.textContent = sorte ? `${produkt}, ${sorte}` : produkt;
      else this.name.textContent = sorte || produkt;
    }

    if (this.image && variant.featured_image?.src) {
      try {
        const url = new URL(variant.featured_image.src, window.location.origin);
        url.searchParams.set('width', '200');
        this.image.src = url.toString();
        this.image.removeAttribute('srcset');
      } catch {
        /* Bild bleibt beim vorherigen Stand */
      }
    }

    /* Preis aus dem Kaufbereich uebernehmen, damit beide immer dasselbe zeigen */
    const quellePreis = document.querySelector('[data-price] .price');
    if (quellePreis && this.priceBox) {
      const kopie = quellePreis.cloneNode(true);
      kopie.classList.add('price--compact');
      if (this.hasAttribute('data-hide-note')) kopie.querySelector('.price__tax')?.remove();
      if (this.hasAttribute('data-hide-serving')) kopie.querySelector('.price__serving')?.remove();
      if (this.hasAttribute('data-hide-badge')) kopie.querySelector('.price__badge')?.remove();
      if (this.hasAttribute('data-hide-unit')) kopie.querySelector('.price__unit')?.remove();
      this.priceBox.replaceChildren(kopie);
    }

    /* Der Schnellkauf soll mit derselben Sorte aufgehen */
    const stift = this.querySelector('[data-sticky-edit]');
    if (stift) stift.dataset.quickAddVariant = variant.id;

    this.zustandSpiegeln();
  }

  async kaufen() {
    const echt = this.echterKnopf;
    if (!echt) {
      /* Ausverkauft: derselbe Weg wie oben, der Benachrichtigen-Dialog */
      this.zielKnopf?.click();
      return;
    }
    const form = this.form;
    if (!form || echt.disabled || !this.button) return;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    const fertig = () => {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    };
    document.addEventListener('cart:updated', fertig, { once: true });
    window.setTimeout(fertig, 6000);
    form.requestSubmit();
  }
}

customElements.define('kg-sticky-buy', KgStickyBuy);

/*
  Safari ab Version 26 misst die Farbe seiner Leisten nur, wenn sich die oberste Ebene
  aendert — beim blossen Verschwinden der Kaufleiste nicht. Der Balken behielt deshalb ihr
  Weiss.

  Ein Dialog, der kurz auf- und wieder zugeht, erzwingt diese Messung. Entscheidend ist,
  dass er zwei Bildaufbauten offen bleibt: Safari liest das zuletzt gezeichnete Bild, und
  ein im selben Schritt wieder geschlossener Dialog wurde nie gezeichnet.

  Der Dialog ist ein Pixel gross und durchsichtig; der Fokus wird gemerkt und zurueckgegeben.
*/
let messPunkt = null;

function neuMessenLassen() {
  if (!window.matchMedia('(max-width: 1023.98px)').matches) return;
  if (!messPunkt) {
    messPunkt = document.createElement('dialog');
    /* Ohne diese Klasse faerbt die globale Regel seinen Backdrop ein — der ganze Schirm
       dunkelte dann fuer zwei Bildaufbauten ab, sichtbar als kurzes Aufblitzen. */
    messPunkt.className = 'kg-messpunkt';
    messPunkt.style.cssText =
      'position:fixed;inset:auto auto 0 0;margin:0;width:1px;height:1px;border:0;padding:0;background:transparent;pointer-events:none';
    document.body.append(messPunkt);
  }
  if (messPunkt.open) return;
  const vorher = document.activeElement;
  try {
    messPunkt.showModal();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        messPunkt.close();
        if (vorher && vorher !== document.body) vorher.focus?.({ preventScroll: true });
      })
    );
  } catch {
    /* Laesst sich nicht oeffnen, dann bleibt es beim alten Verhalten. */
  }
}
